import React, { FC } from 'react'

import { ArtboardDetailInfoFragment } from '@sketch/gql-types'
import { IS_EMBEDDED } from '@sketch/constants'
import { ShareWithoutVersion, useVersioning } from 'modules/versioning'
import { versionedRoutes } from '@sketch/modules-common'
import { useForDesktop, useForTablet } from '@sketch/components'
import { Zoom, ZoomTypes } from 'components/Zoom'

import { AddAnnotationNavbarItem } from 'modules/annotations/components'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

import HeaderNavigation from 'modules/shares/components/HeaderNavigation'
import HeaderSidebarToggle from 'modules/shares/components/HeaderSidebarToggle'
import {
  EditInSketchButton,
  OpenTemplateButton,
} from 'modules/shares/components/EditInSketchButton'
import VersionLinkButton from 'modules/shares/components/VersionLinkButton'
import PlayPrototypeLink from 'modules/shares/components/PlayPrototypeLink'

import {
  NavbarItem,
  MiddleContainer,
  AddAnnotationText,
} from './ArtboardDetailHeader.styles'
import { ArtboardDetailSettingsDropdown } from '../ArtboardDetailSettingsDropdown'
import { DocumentSidebarLayoutExtraProps } from 'modules/shares/components/DocumentSidebarLayout'
import { useGridAndLayoutPropForZoomComponent } from '../../hooks'

type DetailHeaderProps = {
  share: ShareWithoutVersion
  artboard?: ArtboardDetailInfoFragment
  artboardLatestVersion: string
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  userCanOpenInApp: boolean
  zoom: ZoomTypes
  setZoom: (newZoom: ZoomTypes) => void
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const ArtboardDetailHeader: FC<DetailHeaderProps> = ({
  share,
  artboard,
  toggleSidebarRight,
  isSidebarRightOpen,
  isPrototypePlayEnabled,
  isViewingLatestVersion,
  userCanOpenInApp,
  zoom,
  setZoom,
}) => {
  const { type } = share
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()
  const { latestVersionId } = useVersioning()
  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED

  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []

  const gridAndLayoutPropsForZoomComponent = useGridAndLayoutPropForZoomComponent()

  const isTemplate = type === 'TEMPLATE'

  if (placeDraftAnnotation && isTabletAndBigger) {
    return (
      <>
        <MiddleContainer />
        <AddAnnotationText>Click on the Artboard to comment</AddAnnotationText>
        <MiddleContainer>
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              gridAndLayout={gridAndLayoutPropsForZoomComponent}
            />
          </NavbarItem>
          <AddAnnotationNavbarItem />
        </MiddleContainer>
        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </>
    )
  }

  const showLatestVersionButton =
    isTabletAndBigger &&
    !isViewingLatestVersion &&
    !IS_EMBEDDED &&
    // never show if user can only access latest version (it will update automatically)
    share.versionTypeAvailableToUser !== 'LATEST'

  const latestVersionPathname = versionedRoutes.ARTBOARD_DETAIL.VERSION.create({
    permanentArtboardShortId: artboard?.permanentArtboardShortId!,
    shareID: share.identifier,
    versionShortId: latestVersionId,
  })

  return (
    <>
      <HeaderNavigation share={share} artboard={artboard} />

      {isTabletAndBigger && (
        <NavbarItem>
          <Zoom
            zoom={zoom}
            setZoom={setZoom}
            gridAndLayout={gridAndLayoutPropsForZoomComponent}
          />
        </NavbarItem>
      )}

      {canOpenInApp && isTemplate && (
        <NavbarItem>
          <OpenTemplateButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {canOpenInApp && !isTemplate && (
        <NavbarItem>
          <EditInSketchButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {artboard && showLatestVersionButton && (
        <NavbarItem>
          <VersionLinkButton to={latestVersionPathname}>
            See Latest Version
          </VersionLinkButton>
        </NavbarItem>
      )}

      {artboard && isTabletAndBigger && artboard.isFlowHome && (
        <NavbarItem>
          <PlayPrototypeLink
            isPrototypePlayEnabled={isPrototypePlayEnabled}
            artboardUUID={artboard.uuid}
            shareIdentifier={share.identifier}
            representation="navbar"
          />
        </NavbarItem>
      )}

      {isTabletAndBigger && <AddAnnotationNavbarItem />}
      <ArtboardDetailSettingsDropdown />

      <HeaderSidebarToggle
        toggleSidebar={toggleSidebarRight}
        isSidebarOpen={isSidebarRightOpen}
      />
    </>
  )
}
